import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../services/axiosInstance";
import ReportsTable from '../components/admin/reports/ReportsTable';
import FilterBar from '../components/CommonComponents/FilterBar';
import DatePicker from "../components/CommonComponents/DatePicker";
import EmptyState from '../components/CommonComponents/EmptyState';
import { BarChart3, Building2, CalendarDays, Clock, Download, FileText, ReceiptText, Users, WalletCards } from 'lucide-react';
import { fetchBranches } from "../services/branchApi";
import { fetchUsers } from "../services/usersApi";
import { useTheme } from "../components/CommonComponents/ThemeProvider";

const formatCurrency = (amount) => {
    if (isNaN(amount)) return "₱0.00";
    return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
    }).format(Number(amount) || 0);
};

export default function Reports() {
    const user = useSelector(state => state.auth.user);
    const { currentTheme } = useTheme();
    const customer_id = user?.customer_id;

    const [deviceTime, setDeviceTime] = useState(() => new Date());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [salesData, setSalesData] = useState([]);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    // Filter states
    const [dateFilter, setDateFilter] = useState("all");
    const [branchFilter, setBranchFilter] = useState("");
    const [cashierFilter, setCashierFilter] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [branches, setBranches] = useState([]);
    const [users, setUsers] = useState([]);
    const dateRangeRef = useRef({ startDate: "", endDate: "" });

    const toLocalStart = useCallback((dateStr) => (dateStr ? `${dateStr}T00:00:00` : ""), []);
    const toLocalEnd = useCallback((dateStr) => (dateStr ? `${dateStr}T23:59:59.999` : ""), []);

    useEffect(() => {
        const timer = setInterval(() => {
            setDeviceTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        dateRangeRef.current = { startDate, endDate };
    }, [startDate, endDate]);

    // Fetch sales report data with date filters
    const fetchReports = useCallback(async (start = "", end = "", branchId = "", cashierId = "") => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                tzOffset: new Date().getTimezoneOffset(),
            };
            if (start && end) {
                params.startDate = toLocalStart(start);
                params.endDate = toLocalEnd(end);
            }
            if (branchId) params.branch_id = Number(branchId);
            if (cashierId) params.user_id = Number(cashierId);
            console.log('Fetching reports with params:', params);
            const response = await axiosInstance.get("/admin/reports/sales", { params });
            console.log('Response data:', response.data);
            console.log('Number of records:', response.data?.length);
            setSalesData(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Failed to fetch sales report:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [toLocalEnd, toLocalStart]);

    // Fetch branches and users for filters, and initial sales data (today)
    useEffect(() => {
        fetchReports(); // Initial load shows today's data

        if (!customer_id) return;

        fetchBranches(customer_id)
            .then(res => setBranches(res.data.data || []))
            .catch((err) => {
                console.error('Failed to fetch branches:', err);
                setBranches([]);
            });

        fetchUsers()
            .then(res => setUsers(res.data.data || []))
            .catch((err) => {
                console.error('Failed to fetch users:', err);
                setUsers([]);
            });
    }, [customer_id, fetchReports]);

    // Handle date filter submission - applies to both table and export
    const handleDateSubmit = () => {
        fetchReports(startDate, endDate, branchFilter, cashierFilter);
    };

    useEffect(() => {
        const currentRange = dateRangeRef.current;
        fetchReports(currentRange.startDate, currentRange.endDate, branchFilter, cashierFilter);
    }, [branchFilter, cashierFilter, fetchReports]);

    const getExportParams = () => {
        const params = { customer_id, tzOffset: new Date().getTimezoneOffset() };
        if (branchFilter) params.branch_id = Number(branchFilter);
        if (cashierFilter) params.user_id = Number(cashierFilter);
        if (startDate) params.startDate = toLocalStart(startDate);
        if (endDate) params.endDate = toLocalEnd(endDate);
        if (!startDate && !endDate && dateFilter) params.dateFilter = dateFilter;
        return params;
    };

    const getExportDateStr = () => (startDate && endDate
        ? `${startDate}_to_${endDate}`
        : dateFilter || 'all');

    // Bulk transaction export handler (CSV)
    const handleBulkExport = async () => {
        setDownloading(true);
        try {
            const token = sessionStorage.getItem("token") || localStorage.getItem("token");
            const params = getExportParams();

            const response = await axiosInstance.get("admin/orders/export", {
                params,
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', `transactions_${getExportDateStr()}.csv`);

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('CSV download failed:', error);
            alert('Failed to download CSV report. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    // Bulk transaction export handler (PDF)
    const handlePdfExport = async () => {
        setDownloadingPdf(true);
        try {
            const token = sessionStorage.getItem("token") || localStorage.getItem("token");
            const params = getExportParams();

            const response = await axiosInstance.get("admin/orders/export/pdf", {
                params,
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', `transactions_${getExportDateStr()}.pdf`);

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('PDF download failed:', error);
            alert('Failed to download PDF report. Please try again.');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const salesWithBranchName = salesData.map(sale => ({ ...sale, branch: sale.branch_name || '-' }));
    const totalSales = salesData.reduce((sum, sale) => sum + (Number(sale.totalSales) || 0), 0);
    const totalOrders = salesData.reduce((sum, sale) => sum + (Number(sale.ordersCount) || 0), 0);
    const activeBranchLabel = branchFilter
        ? branches.find((branch) => String(branch.id) === branchFilter)?.name || "Selected"
        : "All";
    const activeCashierLabel = cashierFilter
        ? users.find((cashier) => String(cashier.id) === cashierFilter)?.username || "Selected"
        : "All";
    const dateRangeLabel = startDate && endDate ? `${startDate} to ${endDate}` : "Today";
    const summaryCards = [
        {
            label: "Total Sales",
            value: formatCurrency(totalSales),
            hint: "Filtered revenue",
            icon: WalletCards,
        },
        {
            label: "Orders",
            value: totalOrders,
            hint: "Orders in report",
            icon: ReceiptText,
        },
        {
            label: "Branch",
            value: activeBranchLabel,
            hint: "Current scope",
            icon: Building2,
        },
        {
            label: "Cashier",
            value: activeCashierLabel,
            hint: "Current user filter",
            icon: Users,
        },
    ];

    // Filter configurations
    const filterConfigs = [
        {
            type: 'react-select',
            label: 'Branch',
            value: (() => {
                if (!branchFilter) return { value: "", label: "All Branches" };
                const found = (branches || []).find(b => String(b.id) === branchFilter);
                return found ? { value: String(found.id), label: found.name } : { value: branchFilter, label: branchFilter };
            })(),
            onChange: (value) => setBranchFilter(value),
            options: [
                { value: "", label: "All Branches" },
                ...((branches || []).map(branch => ({ value: String(branch.id), label: branch.name })))
            ],
            isSearchable: true,
            placeholder: "All Branches",
            minWidth: '180px',
        },
        // {
        //     type: 'select',
        //     label: 'Date',
        //     value: dateFilter,
        //     onChange: setDateFilter,
        //     options: [
        //         { value: "today", label: "Today" },
        //         { value: "week", label: "This Week" },
        //         { value: "month", label: "This Month" },
        //         { value: "all", label: "All" },
        //     ],
        //     minWidth: '120px',
        // },
        // {
        //     type: 'select',
        //     label: 'Status',
        //     value: statusFilter,
        //     onChange: setStatusFilter,
        //     options: [
        //         { value: "", label: "All Status" },
        //         { value: "PAID", label: "Paid" },
        //         { value: "Pending", label: "Pending" },
        //         { value: "Cancelled", label: "Cancelled" },
        //     ],
        //     minWidth: '120px',
        // },
        {
            type: 'react-select',
            label: 'Cashier',
            value: (() => {
                if (!cashierFilter) return { value: "", label: "All Cashiers" };
                const found = (users || []).find(u => String(u.id) === cashierFilter);
                return found ? { value: String(found.id), label: found.username || found.email } : { value: cashierFilter, label: cashierFilter };
            })(),
            onChange: (value) => setCashierFilter(value),
            options: [
                { value: "", label: "All Cashiers" },
                ...((users || []).map(user => ({
                    value: String(user.id),
                    label: user.username || user.email
                })))
            ],
            isSearchable: true,
            placeholder: "All Cashiers",
            minWidth: '180px',
        },
    ];

    return (
        <div className="min-h-screen bg-[#F8F9FA] pb-10 pt-3 md:pb-12">
            <div className="flex flex-col gap-4">
                <section className="relative overflow-hidden rounded-[30px] border border-slate-200/85 bg-[linear-gradient(145deg,#fffafa,#fffdfd,#ffffff)] p-5 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-6">
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl" style={{ background: "color-mix(in srgb, var(--color-primary) 18%, transparent)" }} />
                        <div className="absolute left-10 top-0 h-28 w-28 rounded-full bg-white/60 blur-2xl" />
                    </div>

                    <div className="relative flex flex-col gap-5">
                        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                            <div className="min-w-0">
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm">
                                    <BarChart3 className="h-3.5 w-3.5" />
                                    Reporting Workspace
                                </div>
                                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                                    Reports
                                </h1>
                                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500 sm:text-[15px]">
                                    Review sales performance, filter transaction summaries, and export reports for reconciliation.
                                </p>
                            </div>
                            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/85 px-4 py-3 text-sm text-slate-600 shadow-sm">
                                <Clock className="h-4 w-4 text-slate-400" />
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Device Time</p>
                                    <p className="font-semibold text-slate-800">{deviceTime.toLocaleString()}</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                            {summaryCards.map(({ label, value, hint, icon: Icon }) => (
                                <div key={label} className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                        <Icon className="h-4 w-4" />
                                        {label}
                                    </div>
                                    <p className="mt-3 truncate text-2xl font-semibold text-slate-900">{value}</p>
                                    <p className="mt-1 truncate text-xs text-slate-500">{hint}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6">
                    <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                        <div>
                            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
                                <CalendarDays className="h-5 w-5" />
                            </div>
                            <h2 className="mt-4 text-lg font-semibold text-slate-950">Date Range</h2>
                            <p className="mt-1 text-sm text-slate-500">Controls both the on-screen table and exported reports.</p>
                            <p className="mt-3 inline-flex rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-500">
                                Current range: {dateRangeLabel}
                            </p>
                        </div>

                        <div className="grid w-full gap-3 rounded-[24px] border border-slate-200 bg-slate-50/70 p-4 sm:grid-cols-[1fr_auto_1fr] xl:max-w-3xl">
                            <label className="flex min-w-0 flex-col gap-1 text-sm font-semibold text-slate-600">
                                From
                                <DatePicker
                                    value={startDate}
                                    onChange={(dateValue) => {
                                        setStartDate(dateValue);
                                        setDateFilter("");
                                    }}
                                    maxDate={endDate}
                                    themeColor={currentTheme?.color || "#0f172a"}
                                    placeholder="Select start date"
                                    ariaLabel="Select report start date"
                                />
                            </label>
                            <span className="hidden self-end pb-3 text-sm font-semibold text-slate-400 sm:block">to</span>
                            <label className="flex min-w-0 flex-col gap-1 text-sm font-semibold text-slate-600">
                                To
                                <DatePicker
                                    value={endDate}
                                    onChange={(dateValue) => {
                                        setEndDate(dateValue);
                                        setDateFilter("");
                                    }}
                                    minDate={startDate}
                                    themeColor={currentTheme?.color || "#0f172a"}
                                    placeholder="Select end date"
                                    ariaLabel="Select report end date"
                                />
                            </label>
                            <div className="flex flex-wrap items-center gap-2 sm:col-span-3 sm:justify-end">
                                {!startDate && !endDate && (
                                    <span className="mr-auto text-sm text-slate-500">Showing today's data</span>
                                )}
                                {(startDate || endDate) && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setStartDate("");
                                            setEndDate("");
                                            setDateFilter("all");
                                            fetchReports();
                                        }}
                                        className="h-11 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-100"
                                    >
                                        Clear
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleDateSubmit}
                                    disabled={!startDate || !endDate}
                                    className="h-11 rounded-2xl px-5 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                                    style={!startDate || !endDate ? undefined : { background: "var(--color-primary)" }}
                                >
                                    Apply Range
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6">
                    <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-950">Export Transactions</h2>
                            <p className="mt-1 text-sm text-slate-500">Apply branch or cashier filters, then download the selected date range.</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={handleBulkExport}
                                disabled={downloading || !startDate || !endDate}
                                className="inline-flex h-11 items-center gap-2 rounded-2xl px-5 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                                style={downloading || !startDate || !endDate ? undefined : { background: "var(--color-primary)" }}
                            >
                                <Download size={18} />
                                {downloading ? 'Downloading...' : 'Download CSV'}
                            </button>
                            <button
                                type="button"
                                onClick={handlePdfExport}
                                disabled={downloadingPdf || !startDate || !endDate}
                                className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                            >
                                <FileText size={18} />
                                {downloadingPdf ? 'Downloading...' : 'Download PDF'}
                            </button>
                        </div>
                    </div>

                    <FilterBar filters={filterConfigs} />
                </section>

                <section className="mb-6 rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6">
                    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-950">Sales Summary</h2>
                            <p className="mt-1 text-sm text-slate-500">Aggregated sales data by date, branch, and cashier.</p>
                        </div>
                        {!loading && !error && (
                            <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-500">
                                {salesData.length} records
                            </div>
                        )}
                    </div>

                    {error && (
                        <EmptyState
                            icon={BarChart3}
                            title="Failed to Load Reports"
                            message="Check your internet connection and try again."
                            onRetry={fetchReports}
                            retryLabel="Reload"
                        />
                    )}
                    {!error && (
                        <ReportsTable
                            sales={salesWithBranchName}
                            loading={loading}
                            error={error}
                        />
                    )}
                </section>
            </div>
        </div>
    );
}
